import React, {
    memo,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
    CaretDownOutlined,
    CaretUpOutlined,
    LeftOutlined,
    MenuOutlined,
    RightOutlined,
} from '@ant-design/icons';
import {
    DndContext,
    type DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    ConfigProvider,
    Empty,
    type GetProp,
    Table as AntTable,
    type TableColumnType,
    type TableProps,
} from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import cn from 'classnames';

import { ColumnName } from '@/shared/cms/column-name';
import { useDebounce } from '@/shared/lib/hooks';
import {
    useGetTableColumnsQuery,
    useUpdateTableColumnsMutation,
} from '@/shared/lib/table';
import { InputToggle } from '@/shared/ui/input';
import { Loader } from '@/shared/ui/loader';
import { Popover } from '@/shared/ui/popover';

import cls from './table.module.css';

type ConfigurableColumnType<T> = TableColumnType<T> & {
    configurable?: boolean;
    disableVisibilityToggle?: boolean;
    order?: number;
    visible?: boolean;
};

interface ExtendedColumns<T> {
    [key: string]: Partial<ConfigurableColumnType<T>>;
}

interface CustomPaginationOptions {
    isPrevDisabled: boolean;
    isNextDisabled: boolean;
    onPrevClick: () => void;
    onNextClick: () => void;
    currentPage: number;
    paginationTextVariable: {
        from: number;
        to: number;
        total: number;
    };
    customPaginationClassName?: string;
    paginationTextKey?: string;
}

interface ConfigurableTableProps<T> extends Omit<TableProps<T>, 'columns'> {
    isConfigOpen?: boolean;
    onConfigClose?: () => void;
    configTriggerRef?: RefObject<HTMLDivElement>;
    onColumnsChange?: (columns: ConfigurableColumnType<T>[]) => void;
    tableId?: string;
    extendedColumns?: ExtendedColumns<T>;
    isConfigOnly?: boolean;
    emptyDescription?: string;
    isSelectable?: boolean;
    isSortable?: boolean;
    sortableColumns?: string[];
    onSort?: (columnKey: string, direction: 'asc' | 'desc' | undefined) => void;
    selectionType?: 'radio' | 'checkbox';
    onSelectionChange?: (
        selectedRowKeys: React.Key[],
        selectedRows: T[],
    ) => void;
    actionTriggerRef?: RefObject<HTMLDivElement>;
    onAction?: (selectedRows: T[]) => void;
    selectedRowKeys?: React.Key[];
    onDragEnd?: (draggedId: string, droppedId: string) => void;
    isDraggable?: boolean;
    totalCount?: number;
    maxSelectableRows?: number;
    customPagination?: CustomPaginationOptions;
    sort?: {
        columnKey?: string | undefined;
        direction?: 'asc' | 'desc' | undefined;
    };
}

interface SortableItemProps {
    children: React.ReactNode;
    id: string;
}

const SortableItem = memo(({ children, id }: SortableItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            {...attributes}
            ref={setNodeRef}
            className={cls.columnConfigItem}
            style={style}
        >
            <div className={cls.columnConfigRow}>
                <div
                    {...listeners}
                    ref={setActivatorNodeRef}
                    className={cls.dragHandle}
                >
                    <MenuOutlined />
                </div>
                {children}
            </div>
        </div>
    );
});

SortableItem.displayName = 'SortableItem';

function ConfigurableTableComponent<T extends object>(
    props: ConfigurableTableProps<T>,
) {
    const {
        isConfigOpen,
        onConfigClose,
        configTriggerRef,
        onColumnsChange,
        tableId,
        extendedColumns,
        isConfigOnly,
        isSelectable = false,
        selectionType,
        onSelectionChange,
        actionTriggerRef,
        onAction,
        selectedRowKeys,
        onDragEnd,
        isDraggable,
        emptyDescription,
        totalCount,
        maxSelectableRows,
        customPagination,
        loading,
        isSortable,
        sortableColumns = [],
        onSort,
        sort,
        ...tableProps
    } = props;

    const { t } = useTranslation('table');
    const { data: savedPreferences } = useGetTableColumnsQuery(tableId || '', {
        skip: !tableId,
    });
    const [updateTableColumns, { isLoading: isUpdating }] =
        useUpdateTableColumnsMutation();

    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
    const [columns, setColumns] = useState<ConfigurableColumnType<T>[]>([]);
    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    const [sortState, setSortState] = useState<{
        columnKey: string | undefined;
        direction: 'asc' | 'desc' | undefined;
    }>({
        columnKey: undefined,
        direction: undefined,
    });

    useEffect(() => {
        if (sort && (sort.columnKey || sort.direction)) {
            setSortState({
                columnKey: sort.columnKey,
                direction: sort.direction,
            });
        }
    }, [sort]);

    const debouncedUpdateColumns = useDebounce((...args: unknown[]) => {
        const [id, cols] = args as [
            string,
            Record<string, { visible: boolean; order: number }>,
        ];

        updateTableColumns({
            tableId: id,
            columns: cols,
        });
    }, 1000);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
    );

    const getRowKey = useCallback(
        (item: T): string => {
            const rowKey = tableProps.rowKey as
                | string
                | ((record: T) => string);

            if (typeof rowKey === 'function') {
                return rowKey(item);
            }

            return String((item as Record<string, unknown>)[rowKey as string]);
        },
        [tableProps.rowKey],
    );

    // Effect to initialize the columns from the user preferences when tableId is provided
    useEffect(() => {
        if (!tableId || !savedPreferences?.json?.columns) return;

        const columnPreferences = Object.entries(
            savedPreferences.json.columns,
        ).map(([key]) => ({
            title: <ColumnName slug={key} />,
            dataIndex: key,
            key,
            configurable: true,
            ...(extendedColumns?.[key] || {}),
        })) as ConfigurableColumnType<T>[];

        const extendedColumnsNotInPreferences: ConfigurableColumnType<T>[] = [];

        if (extendedColumns) {
            Object.entries(extendedColumns).forEach(([key, column]) => {
                if (!savedPreferences.json.columns[key]) {
                    extendedColumnsNotInPreferences.push({
                        title: <ColumnName slug={key} />,
                        dataIndex: key,
                        key,
                        configurable: true,
                        ...column,
                    });
                }
            });
        }

        const savedVisibleColumns = columnPreferences
            .filter(
                (col) =>
                    savedPreferences.json.columns[col.key as string]?.visible ??
                    true,
            )
            .map((col) => col.key as string);

        const extendedVisibleColumns = extendedColumnsNotInPreferences
            .filter((col) => col.visible !== false)
            .map((col) => col.key as string);

        setVisibleColumns([...savedVisibleColumns, ...extendedVisibleColumns]);

        const reorderedPreferenceColumns = columnPreferences.sort((a, b) => {
            const orderA =
                savedPreferences.json.columns[a.key as string]?.order ??
                Number.MAX_SAFE_INTEGER;
            const orderB =
                savedPreferences.json.columns[b.key as string]?.order ??
                Number.MAX_SAFE_INTEGER;

            return orderA - orderB;
        });

        const reorderedExtendedColumns = extendedColumnsNotInPreferences.sort(
            (a, b) => {
                const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order ?? Number.MAX_SAFE_INTEGER;

                return orderA - orderB;
            },
        );

        const allColumns = [
            ...reorderedPreferenceColumns,
            ...reorderedExtendedColumns,
        ];
        const sortedColumns = allColumns.sort((a, b) => {
            const orderA =
                a.key &&
                savedPreferences.json.columns[a.key as string]?.order !==
                    undefined
                    ? savedPreferences.json.columns[a.key as string]?.order
                    : a.order ?? Number.MAX_SAFE_INTEGER;

            const orderB =
                b.key &&
                savedPreferences.json.columns[b.key as string]?.order !==
                    undefined
                    ? savedPreferences.json.columns[b.key as string]?.order
                    : b.order ?? Number.MAX_SAFE_INTEGER;

            return (
                (orderA ?? Number.MAX_SAFE_INTEGER) -
                (orderB ?? Number.MAX_SAFE_INTEGER)
            );
        });

        setColumns(sortedColumns);
    }, [savedPreferences, t, tableId, extendedColumns]);

    // Effect to initialize columns directly from extendedColumns when tableId is not provided
    useEffect(() => {
        if (tableId || !extendedColumns) return;

        const columnsFromExtended = Object.entries(extendedColumns).map(
            ([key, column]) => ({
                title: <ColumnName slug={key} />,
                dataIndex: key,
                key,
                configurable: true,
                ...column,
            }),
        ) as ConfigurableColumnType<T>[];

        const visibleColumnsFromExtended = columnsFromExtended
            .filter((col) => col.visible !== false)
            .map((col) => col.key as string);

        setVisibleColumns(visibleColumnsFromExtended);

        const sortedColumns = columnsFromExtended.sort((a, b) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;

            return orderA - orderB;
        });

        setColumns(sortedColumns);
    }, [t, tableId, extendedColumns]);

    // Handle the visibility change of a column
    const handleVisibilityChange = useCallback(
        async (columnKey: string) => {
            const newVisibleColumns = visibleColumns.includes(columnKey)
                ? visibleColumns.filter((key) => key !== columnKey)
                : [...visibleColumns, columnKey];

            const alwaysVisibleColumns = columns
                .filter((col) => col.disableVisibilityToggle)
                .map((col) => col.key as string);

            const finalVisibleColumns = alwaysVisibleColumns.reduce(
                (acc, key) => (acc.includes(key) ? acc : [...acc, key]),
                [...newVisibleColumns],
            );

            setVisibleColumns(finalVisibleColumns);

            const newColumns = columns.filter((col) =>
                finalVisibleColumns.includes(col.key as string),
            );

            onColumnsChange?.(newColumns);

            const configurableColumns = columns.filter(
                (col) => !col.disableVisibilityToggle,
            );

            const columnsPreferences = configurableColumns.reduce<
                Record<string, { visible: boolean; order: number }>
            >((acc, col, index) => {
                const key = col.key as string;

                let existingOrder = index;

                if (col.order !== undefined) {
                    existingOrder = col.order;
                } else if (
                    savedPreferences?.json?.columns?.[key]?.order !== undefined
                ) {
                    existingOrder =
                        savedPreferences?.json?.columns?.[key]?.order ??
                        existingOrder;
                }

                return {
                    ...acc,
                    [key]: {
                        visible: finalVisibleColumns.includes(key),
                        order: existingOrder,
                    },
                };
            }, {});

            if (tableId) {
                debouncedUpdateColumns(tableId, columnsPreferences);
            }
        },
        [
            visibleColumns,
            columns,
            onColumnsChange,
            tableId,
            debouncedUpdateColumns,
            savedPreferences,
        ],
    );

    // Handle the drag end event of a column
    const handleColumnDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
                const oldIndex = columns.findIndex(
                    (col) => col.key === active.id,
                );
                const newIndex = columns.findIndex(
                    (col) => col.key === over?.id,
                );

                const newColumns = [...columns];
                const [movedColumn] = newColumns.splice(oldIndex, 1);

                if (movedColumn) {
                    newColumns.splice(
                        newIndex,
                        0,
                        movedColumn as ConfigurableColumnType<T>,
                    );

                    const updatedColumns = newColumns.map((col, index) => ({
                        ...col,
                        order: index,
                    }));

                    setColumns(updatedColumns);

                    const visibleNewColumns = updatedColumns.filter((col) =>
                        visibleColumns.includes(col.key as string),
                    );

                    onColumnsChange?.(visibleNewColumns);

                    const configurableColumns = updatedColumns.filter(
                        (col) => !col.disableVisibilityToggle,
                    );

                    const columnsPreferences = configurableColumns.reduce<
                        Record<string, { visible: boolean; order: number }>
                    >((acc, col) => {
                        const key = col.key as string;

                        return {
                            ...acc,
                            [key]: {
                                visible: visibleColumns.includes(key),
                                order: col.order ?? 0,
                            },
                        };
                    }, {});

                    if (tableId) {
                        debouncedUpdateColumns(tableId, columnsPreferences);
                    }
                }
            }
        },
        [
            columns,
            visibleColumns,
            onColumnsChange,
            tableId,
            debouncedUpdateColumns,
        ],
    );

    // Handle table row selection
    const handleRowSelection = useCallback(
        (keys: React.Key[], selectedTableRows: T[]) => {
            if (maxSelectableRows && keys.length > maxSelectableRows) {
                const limitedKeys = keys.slice(0, maxSelectableRows);
                const limitedRows = selectedTableRows.slice(
                    0,
                    maxSelectableRows,
                );

                setSelectedRows(limitedRows);
                onSelectionChange?.(limitedKeys, limitedRows);

                return;
            }

            setSelectedRows(selectedTableRows);
            onSelectionChange?.(keys, selectedTableRows);
        },
        [onSelectionChange, maxSelectableRows],
    );

    // Handle the action trigger
    const handleAction = useCallback(() => {
        if (selectedRows.length > 0 && onAction) {
            onAction(selectedRows);
        }
    }, [selectedRows, onAction]);

    // Handle column sort
    const handleColumnSort = useCallback(
        (columnKey: string) => {
            let newDirection: 'asc' | 'desc' | undefined;

            if (sortState.columnKey === columnKey) {
                // Toggle sort direction or clear if already descending
                if (sortState.direction === 'asc') {
                    newDirection = 'desc';
                } else if (sortState.direction === 'desc') {
                    newDirection = undefined;
                } else {
                    newDirection = 'asc';
                }
            } else {
                // New column, start with ascending
                newDirection = 'asc';
            }

            setSortState({
                columnKey: newDirection ? columnKey : undefined,
                direction: newDirection,
            });

            if (onSort) {
                onSort(columnKey, newDirection);
            }
        },
        [sortState, onSort],
    );

    // Render the column configuration
    const columnConfig = useMemo(
        () => (
            <div className={cls.columnConfig}>
                <div className={cls.columnConfigHeader}>
                    {t('columns.visible')}:
                </div>
                <DndContext
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleColumnDragEnd}
                    sensors={sensors}
                >
                    <SortableContext
                        items={columns.map((col) => col.key as string)}
                        strategy={verticalListSortingStrategy}
                    >
                        {columns
                            .filter((column) => !column.disableVisibilityToggle)
                            .map(
                                (column) =>
                                    column.configurable !== false && (
                                        <SortableItem
                                            key={column.key as string}
                                            id={column.key as string}
                                        >
                                            <InputToggle
                                                isChecked={visibleColumns.includes(
                                                    column.key as string,
                                                )}
                                                isDisabled={isUpdating}
                                                onChange={() =>
                                                    handleVisibilityChange(
                                                        column.key as string,
                                                    )
                                                }
                                            >
                                                {(() => {
                                                    const { title } = column;

                                                    if (
                                                        React.isValidElement(
                                                            title,
                                                        )
                                                    ) {
                                                        // runtime type check for ColumnName component
                                                        if (
                                                            (
                                                                title.type as React.ComponentType & {
                                                                    displayName?: string;
                                                                }
                                                            )?.displayName ===
                                                            'ColumnName'
                                                        ) {
                                                            return React.cloneElement(
                                                                title as React.ReactElement,
                                                                {
                                                                    shouldShowInfoIcon:
                                                                        false,
                                                                },
                                                            );
                                                        }

                                                        return title;
                                                    }

                                                    if (
                                                        typeof title ===
                                                        'function'
                                                    )
                                                        return String(title);

                                                    return String(title ?? '');
                                                })()}
                                            </InputToggle>
                                        </SortableItem>
                                    ),
                            )}
                    </SortableContext>
                </DndContext>
            </div>
        ),
        [
            columns,
            visibleColumns,
            handleVisibilityChange,
            handleColumnDragEnd,
            sensors,
            t,
            isUpdating,
        ],
    );

    // Render the visible columns list
    const visibleColumnsList = useMemo(
        () =>
            columns.filter((col) => visibleColumns.includes(col.key as string)),
        [columns, visibleColumns],
    );

    // Render the visible columns list with sort indicators
    const visibleColumnsWithSortIndicators = useMemo(
        () =>
            visibleColumnsList.map((column) => {
                if (
                    !isSortable ||
                    !sortableColumns.includes(column.key as string)
                ) {
                    return column;
                }

                const isCurrentSortColumn = sortState.columnKey === column.key;
                const originalTitle = column.title as React.ReactNode;

                return {
                    ...column,
                    title: (
                        <div
                            className={cls.columnTitleWrapper}
                            onClick={() =>
                                handleColumnSort(column.key as string)
                            }
                        >
                            <span
                                className={cn(cls.columnTitle, {
                                    [cls.columnTitleActive]:
                                        isCurrentSortColumn,
                                })}
                            >
                                {originalTitle}
                            </span>
                            <span className={cls.sortIcons}>
                                <CaretUpOutlined
                                    className={cn(cls.sortIcon, {
                                        [cls.sortIconActive]:
                                            isCurrentSortColumn &&
                                            sortState.direction === 'asc',
                                    })}
                                />
                                <CaretDownOutlined
                                    className={cn(cls.sortIcon, {
                                        [cls.sortIconActive]:
                                            isCurrentSortColumn &&
                                            sortState.direction === 'desc',
                                    })}
                                />
                            </span>
                        </div>
                    ),
                    className: cn(column.className, {
                        [cls.sortableColumn]: true,
                        [cls.sortedColumn]: isCurrentSortColumn,
                        [cls.sortedColumnAsc]:
                            isCurrentSortColumn &&
                            sortState.direction === 'asc',
                        [cls.sortedColumnDesc]:
                            isCurrentSortColumn &&
                            sortState.direction === 'desc',
                    }),
                };
            }),
        [
            visibleColumnsList,
            isSortable,
            sortableColumns,
            sortState,
            handleColumnSort,
        ],
    );

    // Render the visible columns list with the drag handle
    const visibleColumnsWithDragHandle = isDraggable
        ? [
              {
                  title: '',
                  dataIndex: 'drag-handle',
                  key: 'drag-handle',
                  width: 40,
                  className: cls.dragHandleColumn,
                  render: () => (
                      <div className={cls.tableDragHandle}>
                          <MenuOutlined />
                      </div>
                  ),
              },
              ...visibleColumnsWithSortIndicators,
          ]
        : visibleColumnsWithSortIndicators;

    // Add sort button if table is sortable
    const tableClassName = cn({
        [cls.selectable]: isSelectable,
        [cls.sortable]: isSortable,
    });

    // Create the custom the loading indicator with our Loader component
    const customLoadingIndicator = (
        <div className={cls.tableLoaderWrapper}>
            <Loader size="medium" />
        </div>
    );

    // Render the table props with selection
    const tablePropsWithSelection: TableProps<T> = {
        ...tableProps,
        columns: visibleColumnsWithDragHandle,
        scroll: { x: 'max-content' },
        className: tableClassName,
        loading: loading
            ? {
                  indicator: customLoadingIndicator,
                  size: 'default',
                  spinning: true,
              }
            : {
                  indicator: customLoadingIndicator,
                  size: 'default',
                  spinning: false,
              },
        locale: {
            emptyText: '',
        },
    };

    const renderEmpty: GetProp<typeof ConfigProvider, 'renderEmpty'> = () =>
        loading ? (
            <div style={{ height: 200 }} />
        ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        );

    if (isSelectable) {
        const rowSelection = {
            type: selectionType || 'checkbox',
            onChange: handleRowSelection,
            preserveSelectedRowKeys: true,
        } as TableRowSelection<T>;

        if (selectedRowKeys) {
            rowSelection.selectedRowKeys = selectedRowKeys;
        }

        if (maxSelectableRows) {
            rowSelection.getCheckboxProps = (record: T) => {
                const hasReachedLimit = selectedRowKeys
                    ? selectedRowKeys.length >= maxSelectableRows
                    : false;

                const isRowAlreadySelected = selectedRowKeys
                    ? selectedRowKeys.includes(getRowKey(record))
                    : false;

                return {
                    disabled: hasReachedLimit && !isRowAlreadySelected,
                };
            };
        }

        tablePropsWithSelection.rowSelection = rowSelection;
    }

    if (isConfigOnly) {
        return columnConfig;
    }

    // Render custom pagination
    const renderCustomPagination = () => {
        if (!customPagination) return null;

        const {
            isPrevDisabled,
            isNextDisabled,
            onPrevClick,
            onNextClick,
            currentPage,
            paginationTextVariable,
            customPaginationClassName,
            paginationTextKey,
        } = customPagination;

        return (
            <div
                className={`${cls.customPagination} ${customPaginationClassName || ''}`}
            >
                <button
                    aria-label={t('pagination.prev')}
                    className={cls.paginationButton}
                    disabled={isPrevDisabled}
                    onClick={onPrevClick}
                    type="button"
                >
                    <LeftOutlined className={cls.paginationIcon} />
                </button>

                {currentPage && (
                    <span className={cls.paginationPage}>{currentPage}</span>
                )}

                <button
                    aria-label={t('pagination.next')}
                    className={cls.paginationButton}
                    disabled={isNextDisabled}
                    onClick={onNextClick}
                    type="button"
                >
                    <RightOutlined className={cls.paginationIcon} />
                </button>

                {paginationTextVariable && (
                    <span className={cls.paginationText}>
                        {t(
                            paginationTextKey || 'pagination.text',
                            paginationTextVariable,
                        )}
                    </span>
                )}
            </div>
        );
    };

    // Render the preconfigured antd table
    return (
        <>
            {configTriggerRef && (
                <Popover
                    content={columnConfig}
                    trigger={<div ref={configTriggerRef} />}
                />
            )}
            {actionTriggerRef && (
                <Popover
                    content={
                        <div
                            className={cls.actionTrigger}
                            onClick={handleAction}
                        >
                            {t('action.save')}
                        </div>
                    }
                    trigger={<div ref={actionTriggerRef} />}
                />
            )}

            <ConfigProvider renderEmpty={renderEmpty}>
                <AntTable<T> {...tablePropsWithSelection} />
            </ConfigProvider>

            {customPagination && renderCustomPagination()}
        </>
    );
}

type ConfigurableTableType = <T extends object>(
    props: ConfigurableTableProps<T>,
) => JSX.Element;
export const ConfigurableTable = memo(
    ConfigurableTableComponent,
) as unknown as ConfigurableTableType;

(ConfigurableTable as { displayName?: string }).displayName =
    'ConfigurableTable';
